import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Tbody, Td, Th, Thead, Tr, Table, TableContainer, Text } from '@chakra-ui/react'
import { capabilities } from '@homevest/utils'
import { StoreState } from 'store'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { useRentRollRecurringCreditsDataQuery } from 'graphql/generated'
import { formatMoney } from 'lib/numbers'
import { hasCapability } from 'lib/admin-perms'
import { DeleteButton } from './IssueCreditModal/RecurringCreditsDeleteButton'
import IssueCreditModal from './IssueCreditModal'

const { LEDGER_RENT_CREDIT_ISSUER } = capabilities.CAPABILITY_TYPES

export default function RecurringCredits({ rentalId }: { rentalId: string }) {
  const [isCreateRecurringCreditModalOpen, setIsCreateRecurringCreditModalOpen] = useState(false)
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const canCreateRentCredits = hasCapability(admin, LEDGER_RENT_CREDIT_ISSUER)
  const queryContext = useMemo(() => ({ suspense: false }), [])
  const [{ data, fetching, error }, refetchRecurringCredits] = useRentRollRecurringCreditsDataQuery(
    {
      variables: { rentalId },
      requestPolicy: 'cache-and-network',
      context: queryContext
    }
  )
  const rental = data?.rentals_by_pk

  const refreshRecurringCredits = () => {
    refetchRecurringCredits({ requestPolicy: 'network-only' })
  }

  if (fetching && !rental) {
    return (
      <ContentSectionCard title={'Recurring Credits'}>
        <Text color='gray.500'>Loading recurring credits...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !rental) {
    return (
      <ContentSectionCard title={'Recurring Credits'}>
        <Text color='red.600'>Unable to load recurring credits.</Text>
      </ContentSectionCard>
    )
  }

  if (!rental) {
    return null
  }

  if (fetching && !Array.isArray(rental.recurring_credits)) {
    return (
      <ContentSectionCard title={'Recurring Credits'}>
        <Text color='gray.500'>Loading recurring credits...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !Array.isArray(rental.recurring_credits)) {
    return (
      <ContentSectionCard title={'Recurring Credits'}>
        <Text color='red.600'>Unable to load recurring credits.</Text>
      </ContentSectionCard>
    )
  }

  if (!Array.isArray(rental.recurring_credits)) {
    return null
  }

  const { recurring_credits } = rental

  return (
    <ContentSectionCard
      title={'Recurring Credits'}
      action={
        canCreateRentCredits ? (
          <Button
            size='xs'
            colorScheme='teal'
            onClick={(e) => {
              e.stopPropagation()
              setIsCreateRecurringCreditModalOpen(true)
            }}
          >
            + Recurring Credit
          </Button>
        ) : undefined
      }
    >
      {recurring_credits.length > 0 && (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Start Date</Th>
                <Th># Months</Th>
                <Th>Total Amount</Th>
                <Th>Monthly Amount</Th>
                <Th>Liability Type</Th>
                <Th>Notes</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {recurring_credits.map((recurring_credit) => (
                <Tr key={recurring_credit.id}>
                  <Td>{recurring_credit.start_date}</Td>
                  <Td>{recurring_credit.num_months}</Td>
                  <Td>{formatMoney(recurring_credit.total_amount, 2, '$')}</Td>
                  <Td>
                    {formatMoney(
                      Number(
                        (
                          Number(recurring_credit.total_amount) / recurring_credit.num_months
                        ).toFixed(2)
                      ),
                      2,
                      '$'
                    )}
                  </Td>
                  <Td>{recurring_credit.liability_type.name}</Td>
                  <Td>{recurring_credit.notes}</Td>
                  <Td>
                    <DeleteButton
                      recurringCreditId={recurring_credit.id}
                      onSuccess={refreshRecurringCredits}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      {canCreateRentCredits && (
        <IssueCreditModal
          availableTabs={['recurring']}
          close={() => setIsCreateRecurringCreditModalOpen(false)}
          initialTab='recurring'
          isOpen={isCreateRecurringCreditModalOpen}
          onSuccess={refreshRecurringCredits}
          rentalId={rental.id}
        />
      )}
    </ContentSectionCard>
  )
}
